import { useEffect, useMemo, useState } from 'react'
import { loadQueueFilters, saveQueueFilters } from './queueFilterStorage'
import type { ClientCategory, RequestItemPriority } from '../../shared/api/types'

/**
 * Состояние и персистенс фильтров очереди квот. Вынесено из
 * ProcurementQueuePage.tsx (рефакторинг 21.05.2026) без изменения поведения.
 *
 * Приоритеты инициализации:
 *  1. preset из URL (KPI-карточка дашборда) — overrides любых сохранённых.
 *  2. localStorage (загружен на mount) — фильтр выживает переход на страницу
 *     позиции и возврат назад.
 *  3. Дефолты (всё выключено).
 */
export function useQueueFilters(preset: string | null) {
  const stored = useMemo(() => (preset ? null : loadQueueFilters()), [preset])

  const [assignedToMe, setAssignedToMe] = useState(() => stored?.assignedToMe ?? false)
  const [inQueue, setInQueue] = useState(() => stored?.inQueue ?? false)
  const [overdue, setOverdue] = useState(() => stored?.overdue ?? false)
  // «Горящие»/«Без движения» — те же критерии, что KPI-карточки дашборда.
  // Раньше preset 'burning' включал «Просрочено» (дедлайн в прошлом) — карточка
  // «Горящих» (дедлайн в БУДУЩЕМ / HIGH) вела на пустую выборку.
  const [burning, setBurning] = useState(() => stored?.burning ?? preset === 'burning')
  const [stale, setStale] = useState(() => stored?.stale ?? preset === 'stale')
  const [needsInfo, setNeedsInfo] = useState(() => stored?.needsInfo ?? false)
  const [rework, setRework] = useState(() => stored?.rework ?? false)
  const [procStateFilter, setProcStateFilter] = useState<'NEW' | 'ACTIVE' | null>(
    () =>
      stored?.procStateFilter ??
      (preset === 'mine-new' ? 'NEW' : preset === 'mine-waiting' ? 'ACTIVE' : null),
  )
  const [category, setCategory] = useState<ClientCategory | ''>(() => stored?.category ?? '')
  const [priority, setPriority] = useState<RequestItemPriority | ''>(() => stored?.priority ?? '')
  const [searchText, setSearchText] = useState(() => stored?.searchText ?? '')

  useEffect(() => {
    saveQueueFilters({
      assignedToMe,
      inQueue,
      overdue,
      burning,
      stale,
      needsInfo,
      rework,
      procStateFilter,
      category,
      priority,
      searchText,
    })
  }, [
    assignedToMe,
    inQueue,
    overdue,
    burning,
    stale,
    needsInfo,
    rework,
    procStateFilter,
    category,
    priority,
    searchText,
  ])

  return {
    assignedToMe,
    setAssignedToMe,
    inQueue,
    setInQueue,
    overdue,
    setOverdue,
    burning,
    setBurning,
    stale,
    setStale,
    needsInfo,
    setNeedsInfo,
    rework,
    setRework,
    procStateFilter,
    setProcStateFilter,
    category,
    setCategory,
    priority,
    setPriority,
    searchText,
    setSearchText,
  }
}
